import { useEffect, useState } from 'react';
import { ShieldCheck, ShieldAlert, Loader2 } from 'lucide-react';
import Modal from '@/components/ui/Modal';
import { getCachedSession, markSessionVerified } from '@/lib/secretChat/session';

interface Props {
  open: boolean;
  chatId: string;
  peerName: string;
  onClose: () => void;
}

/**
 * Safety-number verification modal.
 *
 * Shows the human-comparable fingerprint of the {our identity, peer identity}
 * pair and lets the user mark the session as manually verified. The number
 * is computed once at session creation and cached on the StoredSession, so
 * we just read it back from IDB.
 *
 * Out-of-band comparison (in person, on a call, over a separate channel) is
 * the *only* way to detect a man-in-the-middle by a malicious server. The
 * UI must NOT auto-verify.
 */
export default function SafetyNumberModal({ open, chatId, peerName, onClose }: Props) {
  const [loading, setLoading] = useState(true);
  const [number, setNumber] = useState<string | null>(null);
  const [verified, setVerified] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    getCachedSession(chatId)
      .then((s) => {
        if (cancelled) return;
        if (!s) {
          setNumber(null);
          setVerified(false);
        } else {
          setNumber(s.safetyNumber);
          setVerified(s.verified);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, chatId]);

  const toggleVerified = async () => {
    const next = !verified;
    setVerified(next);
    await markSessionVerified(chatId, next);
  };

  return (
    <Modal open={open} onClose={onClose} title="Код безопасности">
      {loading ? (
        <div className="flex items-center justify-center py-8 text-surface-500 text-sm">
          <Loader2 size={16} className="animate-spin mr-2" /> Загрузка...
        </div>
      ) : !number ? (
        <div className="text-sm text-surface-400 space-y-3">
          <p>
            Сессия ещё не установлена. Отправьте или получите хотя бы одно сообщение,
            чтобы обменяться ключами.
          </p>
          <button onClick={onClose} className="btn-ghost w-full">Закрыть</button>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-xs text-surface-400 leading-relaxed">
            Сравните этот код с {peerName} лично, по видеосвязи или другим
            доверенным каналом. Если коды совпадают — переписка защищена от
            подмены ключей сервером. Если нет — кто-то посередине.
          </p>

          <div className="bg-surface-950 border border-surface-700 rounded-lg p-4">
            <div className="grid grid-cols-3 gap-2 font-mono text-center text-surface-100 text-base tracking-wider">
              {number.split(' ').map((g, i) => (
                <span key={i}>{g}</span>
              ))}
            </div>
          </div>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={verified}
              onChange={toggleVerified}
              className="mt-1 accent-brand-500"
            />
            <span className="text-sm text-surface-200 flex items-center gap-2">
              {verified ? (
                <ShieldCheck size={16} className="text-green-400" />
              ) : (
                <ShieldAlert size={16} className="text-yellow-400" />
              )}
              Я лично подтвердил(а), что коды совпадают
            </span>
          </label>

          <button onClick={onClose} className="btn-primary w-full">Готово</button>
        </div>
      )}
    </Modal>
  );
}
